import Plan from '@/classes/Plan'
import type { SessionData, PlanData } from '@/types'
import localforage from 'localforage'

/**
 * Legacy SessionData interface for migration purposes
 */
export interface LegacySessionData {
	appointmentId: string | null
	plans: Partial<PlanData>[]
	currentSlideIndex: number
	id: string
	slideDeckId: string
	FHGroupMediaId: string
	language: any
	startAt: string | null
	resultedAt?: string | null
	crmFhGroupId?: number
	FuneralHomeLocationId?: string
}

/**
 * Checks if session data is in the legacy format (has plans array)
 */
function isLegacySessionData(data: any): data is LegacySessionData {
	return data && Array.isArray(data.plans)
}

/**
 * Migrates sessions with included plans to sessions with separate plans
 */
export async function migrateSessionData(
	data: LegacySessionData,
	planLocalforage: typeof localforage
): Promise<SessionData> {
	const planIds: string[] = []

	// Migrate each plan to the plans store
	for (const planData of data.plans) {
		if (planData.id) {
			const plan = Plan.deserialize(undefined, planData)
			await planLocalforage.setItem(plan.id, plan)
			planIds.push(plan.id)
		}
	}

	// Return new session data format
	const { plans, ...sessionDataWithoutPlans } = data
	return {
		...sessionDataWithoutPlans,
		planIds,
	}
}

/**
 * Performs one-time migration of all legacy sessions to the new format
 */
export async function migrateAllSessions(
	legacyLocalforage: typeof localforage,
	newLocalforage: typeof localforage,
	planLocalforage: typeof localforage
): Promise<void> {
	const migratedSessions: SessionData[] = []

	// Iterate through all legacy sessions
	await new Promise<void>((resolve) => {
		legacyLocalforage.iterate(
			async (sessionData: any, key: string) => {
				try {
					if (
						key.indexOf('sessions/') !== 0 &&
						key.indexOf('plans/') !== 0 &&
						isLegacySessionData(sessionData)
					) {
						console.debug('Migrating legacy session:', key)
						const migratedData = await migrateSessionData(
							sessionData,
							planLocalforage
						)
						migratedSessions.push(migratedData)
						await legacyLocalforage.removeItem(key)
					}
				} catch (error) {
					console.error('Failed to migrate session:', key, error)
				}
			},
			async () => {
				// Save all migrated sessions to the new store
				for (const sessionData of migratedSessions) {
					await newLocalforage.setItem(sessionData.id, sessionData)
				}

				console.debug(
					`Migration complete: ${migratedSessions.length} sessions migrated`
				)
				resolve()
			}
		)
	})
}

/**
 * Migrate legacy session data to new format and delete legacy store.
 */
export async function planV0ExtractMigration(): Promise<void> {
	// Legacy localforage instance for migration
	const legacyLocalforage = localforage.createInstance({
		name: 'sessions',
		storeName: 'keyvaluepairs',
	})

	try {
		// Check if legacy store exists and has data
		const legacySessionKeys = await legacyLocalforage.keys()

		if (legacySessionKeys.length === 0) {
			console.debug('No legacy sessions found, skipping migration')
			return
		}

		console.debug(
			`Found ${legacySessionKeys.length} legacy sessions, starting migration...`
		)
		await migrateAllSessions(
			legacyLocalforage,
			localforage.createInstance({ name: 'sessions', storeName: 'sessions' }),
			localforage.createInstance({ name: 'sessions', storeName: 'plans' })
		)

		// Completely delete the legacy store after successful migration
		await legacyLocalforage.dropInstance({
			name: 'sessions',
			storeName: 'keyvaluepairs',
		})
		console.debug('Legacy session store cleared after successful migration')
	} catch (error) {
		console.error('Failed to migrate legacy sessions:', error)
		// Don't delete legacy store if migration failed
	}
}
